import { ExportFileType } from '@common/ts/enums';

/**
 * Trigger a browser download for the given Blob.
 *
 * Firefox requires the anchor element to be attached to the document
 * before `.click()` will fire the download — a naked
 * `a.click()` pattern silently no-ops there. Always append/remove from
 * document.body around the click to stay cross-browser safe, and
 * revoke the object URL so the blob is GC'd.
 *
 * This is the single supported path for programmatic downloads in the
 * dashboard. Other download helpers (`downloadFile`, Artifact exports)
 * delegate here.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * Downloads a string payload as a file with an inferred MIME type.
 * Thin wrapper over `downloadBlob` that handles the text-content case.
 */
const downloadFile = (content: string, filename: string): void => {
  const fileExtension = filename.split('.').pop()?.toLowerCase();
  const isCSV = fileExtension === ExportFileType.CSV;
  const mimeType = `text/${isCSV ? ExportFileType.CSV : 'plain'};charset=utf-8;`;
  downloadBlob(new Blob([content], { type: mimeType }), filename);
};

export default downloadFile;
